import axios from 'axios'
import { getAppParams } from '@/stores/app'
import { cacheManager } from '@/utils/cacheManager'
import i18n from '@/i18n'
import { logoutToRN } from '@/bridge'

const t = (key: string) => i18n.global.t(key)

const service = axios.create({
  baseURL: import.meta.env.VITE_API_DOMAIN,
  timeout: 10000,
  headers: {
    'Client-Type': 'h5',
    'Content-Type': 'application/json',
  },
})

/* 请求拦截器 */
service.interceptors.request.use(
  async (config: any) => {
    const { token, language, time_zone: timezone, version, client_id, client_type } = getAppParams()
    if (language) {
      config.headers['Accept-Language'] = language
      config.headers['App-Language'] = language
    }
    if (timezone) config.headers['X-Timezone'] = timezone
    if (token) config.headers['Authorization'] = token
    if (version) config.headers['Version'] = version
    if (client_id) config.headers['Client-Id'] = client_id
    if (client_type) config.headers['Client-Type'] = client_type
    // 海外分支固定区域标识（由 env VITE_REGION_TYPE 注入，值由后端定义）
    config.headers['Region-Type'] = import.meta.env.VITE_REGION_TYPE
    return config
  },
  (err: any) => Promise.reject(err),
)

/* 响应拦截器 */
service.interceptors.response.use(
  async (response: any) => {
    const res = response.data
    // 缓存成功的响应（仅 GET）
    if (response.config.method === 'get') {
      try {
        const cacheKey = cacheManager.generateKey(response.config.url || '', response.config.params)
        await cacheManager.set(cacheKey, res)
      } catch {
        // ignore
      }
    }
    return res
  },
  async (error: any) => {
    const tryGetFromCache = async () => {
      if (error.config?.method !== 'get') return null
      try {
        const cacheKey = cacheManager.generateKey(error.config.url || '', error.config.params)
        const cachedData = await cacheManager.get(cacheKey)
        if (cachedData) return { ...cachedData, _fromCache: true }
      } catch {
        // ignore
      }
      return null
    }

    // 超时
    if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
      return { code: 408, message: t('requestTimeout') }
    }
    // 网络错误 - 尝试缓存
    if (error?.message === 'Network Error') {
      const cached = await tryGetFromCache()
      if (cached) return cached
      return { code: 504, message: t('networkError') }
    }
    // 401 - 通知 RN 登出
    if (error.response?.status === 401) {
      logoutToRN()
      return { code: 401, message: t('authExpired') }
    }
    // 502
    if (error.response?.status === 502) {
      const cached = await tryGetFromCache()
      if (cached) return cached
      return { code: 502, message: t('badGateway') }
    }
    // 504
    if (error.response?.status === 504) {
      const cached = await tryGetFromCache()
      if (cached) return cached
      return { code: 504, message: t('badGateway') }
    }
    return {
      code: error.response?.status || -1,
      message: error?.message || error.response?.data?.message || t('requestFailed'),
    }
  },
)

export function _httpGet(url: string, params = {}, headers = {}, timeout?: number): Promise<any> {
  return service.get(url, { params, headers, timeout })
}
export function _httpPost(url: string, data = {}, params = {}): Promise<any> {
  return service.post(url, data, { params })
}
